import type { ReactNode } from "react";
import { Navigate } from "@tanstack/react-router";
import { cn } from "@synthesis/shared-utils";

import { useClientType } from "../../shared/hooks/use-client-type";
import { useUserRoles } from "../../shared/hooks/use-user-roles";

/** Полноэкранный каркас страницы ЛК администратора услуг. */
export const AdminPageContainer = ({ children, className }: { children: ReactNode; className?: string }) => (
  <main className={cn("flex min-h-0 w-full flex-1 flex-col overflow-y-auto px-8 py-6", className)}>
    <div className="flex w-full min-w-0 flex-1 flex-col">{children}</div>
  </main>
);

/** Белая карточка раздела ЛК с заголовком и подзаголовком. */
export const AdminSectionCard = ({
  title,
  subtitle,
  children,
  className,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}) => (
  <section className={cn("bg-neutrals-white flex min-w-0 flex-1 flex-col gap-6 rounded-xl p-6", className)}>
    {title || subtitle ? (
      <div className="flex flex-col gap-3">
        {title ? <h1 className="heading-h4 text-type-common">{title}</h1> : null}
        {subtitle ? <p className="subtitle-sm text-type-minor">{subtitle}</p> : null}
      </div>
    ) : null}
    {children}
  </section>
);

/** Проверка доступа к ЛК администратора услуг: внутренний контур + роль. */
export const useAdminConsoleAccess = (): boolean => {
  const clientType = useClientType();
  const roles = useUserRoles();

  return clientType === "internal" && roles.isInternalServiceAdministrator;
};

/** Оборачивает страницы ЛК: без доступа — редирект на главную. */
export const AdminConsoleAccessGuard = ({ children }: { children: ReactNode }) => {
  const hasAccess = useAdminConsoleAccess();

  if (!hasAccess) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};
